import { useState } from 'react';
import { C } from '../constants';
import { toDay, toTime, fmtDay } from '../utils';
import { sendPush } from '../push';

// ── Auto-fill helpers ─────────────────────────────────────────────────────────

export function buildObjective(pt) {
  const lines = [];

  // Latest vitals
  const latest = [...(pt.vitalsLog || [])].sort((a, b) =>
    (b.date + b.time).localeCompare(a.date + a.time)
  )[0];

  if (latest) {
    const vParts = [];
    if (latest.vBP)   vParts.push('BP ' + latest.vBP);
    if (latest.vHR)   vParts.push('HR ' + latest.vHR);
    if (latest.vRR)   vParts.push('RR ' + latest.vRR);
    if (latest.vT)    vParts.push('T ' + latest.vT + '°C');
    if (latest.vO2)   vParts.push('SpO₂ ' + latest.vO2 + '%');
    if (latest.vAVPU && latest.vAVPU !== 'A') vParts.push('AVPU ' + latest.vAVPU);
    if (latest.vGCSe && latest.vGCSv && latest.vGCSm) {
      const tot = parseInt(latest.vGCSe) + parseInt(latest.vGCSv) + parseInt(latest.vGCSm);
      vParts.push(`GCS ${tot} (E${latest.vGCSe}V${latest.vGCSv}M${latest.vGCSm})`);
    }
    if (vParts.length) lines.push('Vitals: ' + vParts.join(' | '));
  }

  // Latest flagged labs
  const allLabs = (pt.labsLog || [])
    .sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
  const flagged = allLabs.flatMap(g =>
    (g.entries || []).filter(e => e.flag && e.name && e.value)
      .map(e => e.name + ' ' + e.value + (e.unit || '') + ' (' + e.flag + ')')
  );
  if (flagged.length) lines.push('Flagged labs: ' + flagged.join(', '));

  // Most recent lab set (all values)
  if (allLabs.length) {
    const recent = allLabs[0];
    const labVals = (recent.entries || [])
      .filter(e => e.name && e.value)
      .map(e => e.name + ' ' + e.value + (e.unit || ''));
    if (labVals.length) lines.push('Latest labs (' + (recent.date || '') + '): ' + labVals.join(', '));
  }

  return lines.join('\n');
}

export function buildAssessment(pt) {
  const lines = [];

  // Diagnosis
  if (pt.diagnosis) lines.push(pt.diagnosis);

  // Active issues list
  const active = (pt.issuesList || []).filter(i => !i.resolved);
  if (active.length) {
    lines.push('Active issues:');
    active.forEach(i => lines.push('- ' + (typeof i === 'string' ? i : (i.text || ''))));
  } else if (pt.activeIssues) {
    lines.push(pt.activeIssues);
  }

  return lines.join('\n');
}

// ── SOAP config ───────────────────────────────────────────────────────────────
const SOAP_FIELDS = [
  { k: 's', label: 'Subjective',  color: '#64D2FF', placeholder: 'What the patient reports — symptoms, complaints, how they feel today…',     autoFill: null },
  { k: 'o', label: 'Objective',   color: '#30D158', placeholder: 'Vitals, exam findings, relevant results…',                                   autoFill: 'o'  },
  { k: 'a', label: 'Assessment',  color: '#FF9500', placeholder: 'Clinical impression, working diagnosis, progress…',                          autoFill: 'a'  },
  { k: 'p', label: 'Plan',        color: '#BF5AF2', placeholder: 'Management plan — medications, orders, monitoring, referrals, goals…',        autoFill: null },
];

const SOAPBadge = ({ field, filled }) => (
  <div style={{ width: 20, height: 20, borderRadius: 5, background: filled ? `${field.color}22` : C.bg3, border: `1px solid ${filled ? field.color + '55' : C.bd}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <span style={{ fontSize: 9, fontWeight: 900, color: filled ? field.color : C.dim2 }}>{field.k.toUpperCase()}</span>
  </div>
);

// ── AutoFill button ───────────────────────────────────────────────────────────
function AutoFillBtn({ color, onFill }) {
  const [flashed, setFlashed] = useState(false);
  const handle = () => {
    onFill();
    setFlashed(true);
    setTimeout(() => setFlashed(false), 800);
  };
  return (
    <button onClick={handle} title="Auto-fill from patient data" style={{
      padding: '2px 8px', borderRadius: 5, border: `1px solid ${color}44`,
      background: flashed ? `${color}33` : `${color}11`,
      color: flashed ? color : color + 'AA',
      fontSize: 10, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
      whiteSpace: 'nowrap',
    }}>
      {flashed ? '✓ Filled' : '⚡ Auto-fill'}
    </button>
  );
}

// ── NoteForm (exported for WardRoundView) ─────────────────────────────────────
export function NoteForm({ cur, onSave, onCancel, compact = false }) {
  const [author, setAuthor] = useState('');
  const [vals,   setVals]   = useState({ s: '', o: '', a: '', p: '' });

  const date = toDay();
  const time = toTime();

  const setField = (k, v) => setVals(prev => ({ ...prev, [k]: v }));

  const autoFillMap = { o: buildObjective, a: buildAssessment };

  const doAutoFill = (k) => {
    const built = autoFillMap[k]?.(cur) || '';
    if (!built) return;
    setField(k, built);
  };

  const save = () => {
    if (!vals.s && !vals.o && !vals.a && !vals.p) return;
    onSave({ id: `note${Date.now()}`, date, time, author: author.trim(), ...Object.fromEntries(Object.entries(vals).map(([k, v]) => [k, v.trim()])) });
  };

  const IS = { width: '100%', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', color: C.tx, fontSize: 13, outline: 'none', fontFamily: 'inherit', lineHeight: 1.6, resize: 'vertical' };

  return (
    <div style={{ background: C.bg2, border: '1px solid #2A4A6A', borderRadius: 12, padding: '16px 14px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#7DC4FF' }}>New Progress Note</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{fmtDay(date)} · {time}</div>
        </div>
        {onCancel && (
          <button onClick={onCancel} style={{ background: 'none', border: 'none', color: C.dim, cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
        )}
      </div>

      {/* Physician */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Physician</div>
        <input value={author} onChange={e => setAuthor(e.target.value)} placeholder="Dr. …"
          style={{ ...IS, resize: undefined }} />
      </div>

      {/* SOAP fields */}
      {SOAP_FIELDS.map(f => (
        <div key={f.k} style={{ marginBottom: 10 }}>
          {/* Label row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
            <div style={{ width: 18, height: 18, borderRadius: 5, background: `${f.color}22`, border: `1px solid ${f.color}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 10, fontWeight: 900, color: f.color }}>{f.k.toUpperCase()}</span>
            </div>
            <span style={{ fontSize: 10, fontWeight: 800, color: f.color, textTransform: 'uppercase', letterSpacing: '0.12em', flex: 1 }}>{f.label}</span>
            {f.autoFill && (
              <AutoFillBtn color={f.color} onFill={() => doAutoFill(f.k)} />
            )}
          </div>
          <textarea
            value={vals[f.k]}
            onChange={e => setField(f.k, e.target.value)}
            placeholder={f.placeholder}
            rows={compact ? 2 : 3}
            style={IS}
            onFocus={e => e.target.style.borderColor = f.color}
            onBlur={e => e.target.style.borderColor = C.bd}
          />
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        {onCancel && (
          <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: `1px solid ${C.bd}`, background: 'transparent', color: C.dim, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            Cancel
          </button>
        )}
        <button onClick={save} style={{ flex: 2, padding: '10px 0', borderRadius: 10, border: 'none', background: '#1A3D6A', color: '#7DC4FF', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          💾 Save Note
        </button>
      </div>
    </div>
  );
}

// ── NoteCard (exported for WardRoundView) ─────────────────────────────────────
export function NoteCard({ note, defaultExpanded = false, onDelete }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div style={{ background: C.bg2, border: `1px solid ${C.bd}`, borderRadius: 12, marginBottom: 10, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', padding: '12px 14px', cursor: 'pointer' }} onClick={() => setExpanded(v => !v)}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.tx }}>{fmtDay(note.date)}</span>
              <span style={{ fontSize: 11, color: C.dim }}>{note.time}</span>
            </div>
            {note.author && <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{note.author}</div>}
            <div style={{ display: 'flex', gap: 3, marginTop: 4, flexWrap: 'wrap' }}>
              {SOAP_FIELDS.map(f => <SOAPBadge key={f.k} field={f} filled={!!note[f.k]} />)}
            </div>
          </div>
          <span style={{ color: C.dim, fontSize: 14, marginLeft: 8, flexShrink: 0 }}>{expanded ? '▾' : '▸'}</span>
        </div>
        {onDelete && (
          <button onClick={e => { e.stopPropagation(); onDelete(); }} style={{
            background: 'none', border: 'none', borderLeft: `1px solid ${C.bd}`,
            color: C.dim2, cursor: 'pointer', fontSize: 14, padding: '0 12px',
            alignSelf: 'stretch', display: 'flex', alignItems: 'center', flexShrink: 0,
          }}>🗑</button>
        )}
      </div>

      {expanded && (
        <div style={{ borderTop: `1px solid ${C.bd}`, padding: '14px 14px 10px' }}>
          {SOAP_FIELDS.filter(f => note[f.k]).map(f => (
            <div key={f.k} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                <div style={{ width: 18, height: 18, borderRadius: 5, background: `${f.color}22`, border: `1px solid ${f.color}55`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 10, fontWeight: 900, color: f.color }}>{f.k.toUpperCase()}</span>
                </div>
                <span style={{ fontSize: 10, fontWeight: 800, color: f.color, textTransform: 'uppercase', letterSpacing: '0.12em' }}>{f.label}</span>
              </div>
              <div style={{ fontSize: 13, color: C.tx, lineHeight: 1.7, whiteSpace: 'pre-wrap', paddingLeft: 26 }}>{note[f.k]}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── NotesTab (default export) ─────────────────────────────────────────────────
export default function NotesTab({ cur, patch, shiftCode }) {
  const [showForm, setShowForm] = useState(false);
  const notes = [...(cur.soapNotes || [])].sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));

  const handleSave = (note) => {
    patch(p => ({ ...p, soapNotes: [...(p.soapNotes || []), note] }));
    if (shiftCode) {
      sendPush(shiftCode, '📝 Progress Note',
        (cur.name || 'Patient') + (cur.room ? ' · Rm ' + cur.room : '') +
        ' — Note added' + (note.author ? ' by ' + note.author : ''),
        cur
      );
    }
    setShowForm(false);
  };

  const del = (id) => {
    if (!window.confirm('Delete this note?')) return;
    patch(p => ({ ...p, soapNotes: (p.soapNotes || []).filter(n => n.id !== id) }));
  };

  return (
    <>
      {!showForm && (
        <button onClick={() => setShowForm(true)} style={{
          width: '100%', padding: '11px 0', borderRadius: 10, border: '1px dashed #2A4A6A',
          background: 'transparent', color: '#4A9EFF', fontSize: 13, fontWeight: 700,
          cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
          ＋ New Progress Note
        </button>
      )}

      {showForm && (
        <div style={{ marginBottom: 16 }}>
          <NoteForm cur={cur} onSave={handleSave} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {notes.length > 0 && (
        <div style={{ fontSize: 10, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
          Progress Notes · {notes.length} entr{notes.length !== 1 ? 'ies' : 'y'}
        </div>
      )}

      {notes.length === 0 && !showForm && (
        <div style={{ textAlign: 'center', color: C.dim2, fontSize: 13, padding: '30px 0' }}>No progress notes recorded</div>
      )}

      {notes.map((note, i) => (
        <NoteCard key={note.id} note={note} defaultExpanded={i === 0} onDelete={() => del(note.id)} />
      ))}
    </>
  );
}
